import { observer } from "mobx-react-lite";
import { useGeneralMapStore } from "../stores/GeneralMapStoreContext";
import { Badge } from "@/components/badges/Badge";
import { X } from "lucide-react";
import { FC, MouseEvent, useRef, useState, useLayoutEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CUSTOM_FILTERS } from "@/config/list";

interface SelectedTagProps {
	label: string;
	onClick: (event: MouseEvent<HTMLDivElement>) => void;
}

const SelectedTag: FC<SelectedTagProps> = ({ label, onClick }) => (
	<Badge
		onClick={onClick}
		className="flex items-center gap-0.5 rounded-full pr-1 pl-2 py-0.5 cursor-pointer hover:bg-gray-800 text-[11px]"
		variant="dark"
	>
		<span>{label}</span>
		<X className="h-2.5 w-2.5" />
	</Badge>
);

export const SelectedFilters = observer(() => {
	const generalMapStore = useGeneralMapStore();
	const { mapStore, structureTypes, structureGroups } = generalMapStore;
	const { filters } = mapStore;

	const typeIds = filters.selectedIds(CUSTOM_FILTERS.structureTypesIds);
	const groupIds = filters.selectedIds(CUSTOM_FILTERS.structureGroupIds);
	const networkTypeIds = filters.selectedIds(CUSTOM_FILTERS.networkType);

	const selectedItems = [
		...(structureGroups.data?.items ?? [])
			.filter(group => groupIds.has(String(group.id)))
			.map(group => ({
				label: group.name,
				toggle: () => filters.toggle(CUSTOM_FILTERS.structureGroupIds, group.id),
			})),
		...Array.from(networkTypeIds).map(networkType => ({
			label: networkType,
			toggle: () => filters.toggle(CUSTOM_FILTERS.networkType, networkType),
		})),
		...(structureTypes.data?.items ?? [])
			.filter(type => typeIds.has(String(type.id)))
			.map(type => ({
				label: type.name,
				toggle: () => filters.toggle(CUSTOM_FILTERS.structureTypesIds, type.id),
			})),
	];

	const containerRef = useRef<HTMLDivElement>(null);
	const [height, setHeight] = useState(0);

	useLayoutEffect(() => {
		if (containerRef.current) {
			setHeight(containerRef.current.scrollHeight);
		}
	}, [selectedItems.length]);

	if (selectedItems.length === 0) return null;

	return (
		<AnimatePresence>
			{selectedItems.length > 0 && (
				<motion.div
					initial={{ height: 0, opacity: 0 }}
					animate={{ height, opacity: 1 }}
					exit={{ height: 0, opacity: 0 }}
					transition={{ duration: 0.3 }}
					className="overflow-hidden"
				>
					<div ref={containerRef}>
						<div className="flex items-center justify-between mb-1">
							<span className="text-xs text-gray-400">Selected: {selectedItems.length}</span>
							<button
								type="button"
								onClick={() => filters.clearAll()}
								className="text-xs text-blue-500 hover:underline"
							>
								Clear all
							</button>
						</div>
						<div className="flex flex-wrap gap-1.5">
							{selectedItems.map(({ label, toggle }, index) => (
								<SelectedTag key={`${label}-${index}`} label={label} onClick={toggle} />
							))}
						</div>
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	);
});
